<template>
  <section class="journey-list-details">
    <div class="journey-list-details__container">
      <RebookingUnavailableProductMessage 
        v-if="formattedResponse && formattedResponse.length > 0"
        :journey-id="journeyId" 
      />
      <JourneyListMessage
        v-if="messageHeading || messageText || hasTravelInfo"
        :heading="travelInfoTitle || messageHeading"
        :text="travelInfoContent || messageText"
      />
    </div>
    <h3 class="journey-list-details__title">
      {{ $t('travelPlanner.journeyList.details.title') }}
    </h3>
    <div v-if="formattedResponse && formattedResponse.length">
      <div class="journey-list-details__leg">
        <div class="journey-list-details__line"></div>
        <div class="journey-list-details__station">
          <div class="journey-list-details__time">
            {{ formatTime(formattedResponse[0].departureStation.timestamp) }}
          </div>
          <div class="journey-list-details__dot-container">
            <svg class="journey-list-details__bus-icon">
              <use :xlink:href="`#${buss}`" />
            </svg>
          </div>
          <div class="journey-list-details__station-details">
            <div class="journey-list-details__name title-medium">
              {{ formattedResponse[0].departureStation.name }}
              <div
                v-if="formattedResponse[0].departureStation.metaData"
                class="journey-list-details__meta-data body-small"
              >
                {{ formattedResponse[0].departureStation.metaData }}
              </div>
              <div
                v-if="formattedResponse[0].departureStation.departurePlatform"
                class="journey-list-details__platform body-small"
              >
                {{ $t('travelPlanner.journeyList.details.platform') }}: {{ formattedResponse[0].departureStation.departurePlatform }}
              </div>
            </div>
            <button v-if="totalStations > 0"
              @click="toggleOpen"
              class="journey-list-details__accordion-button"
            >
              {{ totalStations }}
              {{ $t('travelPlanner.journeyList.details.stops') }}
              <span v-if="totalTransfers > 0">
                , {{ totalTransfers }}
                {{
                  $t(
                    totalTransfers === 1
                      ? 'travelPlanner.journeyList.details.transfer'
                      : 'travelPlanner.journeyList.details.transfers'
                  )
                }}
              </span>
              <svg
                class="journey-list-details__chevron"
                :class="{ 'journey-list-details__chevron--open': isOpen }"
              >
                <use :xlink:href="`#${chevron}`" />
              </svg>
            </button>
          </div>
        </div>
        <div
          v-if="!isOpen"
          class="journey-list-details__station"
        >
          <div class="journey-list-details__time">
            {{
              formatTime(
                formattedResponse[formattedResponse.length - 1].arrivalStation
                  .timestamp
              )
            }}
          </div>
          <div class="journey-list-details__dot-container">
            <div class="journey-list-details__dot"></div>
          </div>
          <div class="journey-list-details__name title-medium">
            {{ formattedResponse[formattedResponse.length - 1].arrivalStation.name }}
            <div
              v-if="formattedResponse[formattedResponse.length - 1].arrivalStation.departurePlatform"
              class="journey-list-details__platform body-small"
            >
              {{ $t('travelPlanner.journeyList.details.platform') }}: {{ formattedResponse[formattedResponse.length - 1].arrivalStation.departurePlatform }}
            </div>
          </div>
        </div>
        <div v-if="isOpen">
          <transition-group
            name="journey-list-details__stations"
            appear
          >
            <template
              v-for="(leg, legIndex) in formattedResponse"
              :key="legIndex"
            >
              <template v-if="legIndex === 0">
                <div
                  v-for="(station, index) in leg.viaStations"
                  :key="index"
                  class="journey-list-details__station"
                >
                  <div class="journey-list-details__time">
                    {{ formatTime(station.timestamp) }}
                  </div>
                  <div class="journey-list-details__dot-container">
                    <div class="journey-list-details__dot"></div>
                  </div>
                  <div class="journey-list-details__name body-large">
                    {{ station.name }}
                    <div
                      v-if="station.departurePlatform"
                      class="journey-list-details__platform body-small"
                    >
                      {{ $t('travelPlanner.journeyList.details.platform') }}: {{ station.departurePlatform }}
                    </div>
                  </div>
                </div>
                <div class="journey-list-details__station">
                  <div class="journey-list-details__time">
                    {{ formatTime(leg.arrivalStation.timestamp) }}
                  </div>
                  <div class="journey-list-details__dot-container">
                    <div class="journey-list-details__dot"></div>
                  </div>
                  <div class="journey-list-details__name title-medium">
                    {{ leg.arrivalStation.name }}
                    <div
                      v-if="leg.arrivalStation.departurePlatform"
                      class="journey-list-details__platform body-small"
                    >
                      {{ $t('travelPlanner.journeyList.details.platform') }}: {{ leg.arrivalStation.departurePlatform }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="formattedResponse.length > 1"
                  class="journey-list-details__waiting-time journey-list-details__gap"
                >
                  <div>
                    {{
                      $t('travelPlanner.journeyList.details.waitingTime', {
                        time: formattedResponse[1].waitingTime,
                      })
                    }}
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="journey-list-details__station">
                  <div class="journey-list-details__time">
                    {{ formatTime(leg.departureStation.timestamp) }}
                  </div>
                  <div class="journey-list-details__dot-container">
                    <svg class="journey-list-details__bus-icon">
                      <use :xlink:href="`#${buss}`" />
                    </svg>
                  </div>
                  <div class="journey-list-details__name title-medium">
                    {{ leg.departureStation.name }}
                    <div
                      v-if="leg.departureStation.metaData"
                      class="journey-list-details__meta-data body-small"
                    >
                      {{ leg.departureStation.metaData }}
                    </div>
                    <div
                      v-if="leg.departureStation.departurePlatform"
                      class="journey-list-details__platform body-small"
                    >
                      {{ $t('travelPlanner.journeyList.details.platform') }}: {{ leg.departureStation.departurePlatform }}
                    </div>
                  </div>
                </div>
                <div
                  v-for="(station, index) in leg.viaStations"
                  :key="index"
                  class="journey-list-details__station"
                >
                  <div class="journey-list-details__time">
                    {{ formatTime(station.timestamp) }}
                  </div>
                  <div class="journey-list-details__dot-container">
                    <div class="journey-list-details__dot"></div>
                  </div>
                  <div class="journey-list-details__name body-large">
                    {{ station.name }}
                    <div
                      v-if="station.departurePlatform"
                      class="journey-list-details__platform body-small"
                    >
                      {{ $t('travelPlanner.journeyList.details.platform') }}: {{ station.departurePlatform }}
                    </div>
                  </div>
                </div>
                <div class="journey-list-details__station">
                  <div class="journey-list-details__time">
                    {{ formatTime(leg.arrivalStation.timestamp) }}
                  </div>
                  <div class="journey-list-details__dot-container">
                    <div class="journey-list-details__dot"></div>
                  </div>
                  <div class="journey-list-details__name title-medium">
                    {{ leg.arrivalStation.name }}
                    <div
                      v-if="leg.arrivalStation.departurePlatform"
                      class="journey-list-details__platform body-small"
                    >
                      {{ $t('travelPlanner.journeyList.details.platform') }}: {{ leg.arrivalStation.departurePlatform }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="legIndex < formattedResponse.length - 1"
                  class="journey-list-details__waiting-time journey-list-details__gap"
                >
                  <div>
                    {{
                      $t('travelPlanner.journeyList.details.waitingTime', {
                        time: formattedResponse[legIndex + 1].waitingTime,
                      })
                    }}
                  </div>
                </div>
              </template>
            </template>
          </transition-group>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
  import { defineComponent, ref, watch, computed, onMounted } from 'vue'
  import { useRoute } from 'vue-router'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { searchServices } from '@/services/journeyService'
  import { formatTime } from '@/utils/dateFormatter'
  import RebookingUnavailableProductMessage from './RebookingUnavailableProductMessage.vue'
  import {
    JourneyStation,
    JourneyDetailsLeg,
    JourneyDetailsResponse,
  } from '@/types/searchTypes'
  import buss from '@/assets/icons/buss.svg'
  import chevron from '@/assets/icons/chevron.svg'
  import JourneyListMessage from './JourneyListMessage.vue'

  interface JourneyLeg {
    service_name: string
    service_schedule_date: string
    departure_station: {
      name: string
      departure_timestamp: string
      departure_platform: string
    }
    arrival_station: {
      name: string
      arrival_timestamp: string
      arrival_platform: string
    }
    travel_info?: Array<{
      title: string
      content: string
    }>
  }

  export default defineComponent({
    name: 'JourneyListDetails',
    components: {
      JourneyListMessage,
      RebookingUnavailableProductMessage,
    },
    props: {
      legs: {
        type: Array as () => JourneyLeg[],
        required: true,
      },
      travelDate: {
        type: String,
        required: true,
      },
      serviceName: {
        type: String,
        required: true,
      },
      messageHeading: {
        type: String,
        required: false,
        default: '',
      },
      messageText: {
        type: String,
        required: false,
        default: '',
      },
      travelInfo: {
        type: Array,
        required: false,
        default: () => [],
      },
      departureStation: {
        type: String,
        required: true,
      },
      arrivalStation: {
        type: String,
        required: true,
      },
    },
    emits: ['update-route-codes'],
    setup(props, { emit }) {
      const isOpen = ref(false)
      const formattedResponse = ref<JourneyDetailsLeg[]>([])
      const isLoading = ref(false)
      const route = useRoute()
      const journeyStore = useJourneyStore()
      
      const isRebooking = computed(() => route.query.isRebooking === 'true')
      
      const hasMissingProductTypes = computed(() => 
        isRebooking.value && journeyStore.missingProductTypes && journeyStore.missingProductTypes.length > 0
      )

      const fetchData = async () => {
        if (isLoading.value || !props.legs?.length) return;

        try {
          isLoading.value = true;
          const allLegs: JourneyDetailsLeg[] = [];
          const routeCodes: string[] = [];
          for (const leg of props.legs) {
            const data = await searchServices(
              leg.service_name,
              leg.service_schedule_date.split('T')[0]
            );
            if (!data || !data.data || !data.data.services) {
              throw new Error('No services found in the response');
            }
            const service = data.data.services[0];
            const routeCode = service.meta_data.find((meta: any) => meta.key === 'route_code')?.value || '';
            const routeName = service.meta_data.find((meta: any) => meta.key === 'route_name')?.value || '';
            const fullRouteReference = routeCode && routeName ? `${routeCode}` : routeCode || routeName || null;

            // Map departure station
            const departureStation: JourneyStation = {
              name: leg.departure_station.name,
              timestamp: leg.departure_station.departure_timestamp,
              metaData: fullRouteReference,
              departurePlatform: leg.departure_station.departure_platform || service.departure_station.departure_platform || null,
            };

            // Map arrival station
            const arrivalStation: JourneyStation = {
              name: leg.arrival_station.name,
              timestamp: leg.arrival_station.arrival_timestamp,
              departurePlatform: leg.arrival_station.arrival_platform || service.arrival_station.arrival_platform || null,
            };

            if (departureStation.metaData) {
              routeCodes.push(departureStation.metaData);
            }

            // Map via stations
            const viaStations: JourneyStation[] = service.via_stations
              .filter((station: any) => {
                const stationName = station.name.toLowerCase();
                const departureName = departureStation.name.toLowerCase();
                const arrivalName = arrivalStation.name.toLowerCase();
                if (stationName === departureName || stationName === arrivalName) {
                  return false;
                }

                const extractTimeInMs = (timestamp: string): number => {
                  if (timestamp.includes('T') && (timestamp.includes('+') || timestamp.includes('Z') || timestamp.includes('-'))) {
                    const [datePart, timePart] = timestamp.split('T');
                    const [year, month, day] = datePart.split('-').map(Number);
                    const timeWithoutTZ = timePart.split('+')[0].split('-')[0].split('Z')[0];
                    const [hours, minutes, seconds] = timeWithoutTZ.split(':').map(Number);
                    return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds || 0)).getTime();
                  }
                  return new Date(timestamp).getTime();
                };

                const stationTime = extractTimeInMs(station.arrival_timestamp);
                const departureTime = extractTimeInMs(departureStation.timestamp);
                const arrivalTime = extractTimeInMs(arrivalStation.timestamp);

                return stationTime > departureTime && stationTime < arrivalTime;
              })
              .map((station: any) => ({
                name: station.name,
                timestamp: station.arrival_timestamp,
                departurePlatform: station.departure_platform || station.arrival_platform || null,
              }));

            allLegs.push({
              departureStation,
              viaStations,
              arrivalStation,
              waitingTime: 0,
              travelInfo: leg.travel_info || [],
            });
          }

          emit('update-route-codes', routeCodes);

          for (let i = 1; i < allLegs.length; i++) {
            const prevLeg = allLegs[i - 1];
            const currLeg = allLegs[i];
            const waitingTime = calculateWaitingTime(
              prevLeg.arrivalStation.timestamp,
              currLeg.departureStation.timestamp
            );

            currLeg.waitingTime = waitingTime;
          }

          allLegs[0].waitingTime = 0;

          formattedResponse.value = allLegs;
        } catch (error) {
          console.error('error:', error);
        } finally {
          isLoading.value = false;
        }
      };

      const calculateWaitingTime = (
        prevTimestamp: string,
        currTimestamp: string
      ): number => {
        const extractDateWithNorwegianTime = (timestamp: string): Date => {
          if (timestamp.includes('T') && (timestamp.includes('+') || timestamp.includes('Z') || timestamp.includes('-'))) {
            const [datePart, timePart] = timestamp.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const timeWithoutTZ = timePart.split('+')[0].split('-')[0].split('Z')[0];
            const [hours, minutes, seconds] = timeWithoutTZ.split(':').map(Number);
            const date = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
            return date;
          }
          return new Date(timestamp);
        };
        
        const prevDate = extractDateWithNorwegianTime(prevTimestamp);
        const currDate = extractDateWithNorwegianTime(currTimestamp);
        const diffMs = currDate.getTime() - prevDate.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        return diffMins;
      }

      const totalStations = computed(() => {
        return (
          formattedResponse.value?.reduce(
            (acc, leg) => acc + (leg.viaStations?.length ?? 0),
            0
          ) || 0
        )
      })

      const totalTransfers = computed(() => {
        return formattedResponse.value ? formattedResponse.value.length - 1 : 0
      })

      const toggleOpen = () => {
        isOpen.value = !isOpen.value
      }

      onMounted(() => {
        if (props.legs?.length) {
          fetchData()
        }
      })

      watch(
        () => props.legs,
        (newLegs, oldLegs) => {
          if (
            newLegs?.length &&
            JSON.stringify(newLegs) !== JSON.stringify(oldLegs)
          ) {
            fetchData()
          }
        },
        { deep: true }
      )

      const hasTravelInfo = computed(() => {
        return formattedResponse.value?.some(leg => (leg.travelInfo?.length ?? 0) > 0)
      })

      const travelInfoTitle = computed(() => {
        const firstLegWithInfo = formattedResponse.value?.find(leg => (leg.travelInfo?.length ?? 0) > 0)
        return firstLegWithInfo?.travelInfo?.[0]?.title || ''
      })

      const travelInfoContent = computed(() => {
        const firstLegWithInfo = formattedResponse.value?.find(leg => (leg.travelInfo?.length ?? 0) > 0)
        return firstLegWithInfo?.travelInfo?.[0]?.content || ''
      })
      const journeyId = computed(() => {
        if (!formattedResponse.value || formattedResponse.value.length === 0) {
          return ''
        }
        
        const route = formattedResponse.value[0]
        
        if (props.routeData && props.routeData.id) {
          return props.routeData.id
        }
        
        if (route && route.id) {
          return route.id
        } else if (route && route.departureStation && route.arrivalStation) {
          let constructedId = `${route.departureStation.name}-${route.arrivalStation.name}`
          
          if (props.routeData && props.routeData.id && props.routeData.id.includes('|')) {
            const routeParts = props.routeData.id.split('|')
            if (routeParts.length >= 2) {
              const routeNumber = routeParts[1]
              constructedId += `|${routeNumber}`
            }
          }
          
          if (route.departureTime) {
            constructedId += `|${route.departureTime}`
          } else if (props.routeData && props.routeData.id && props.routeData.id.includes('T')) {
            const timeMatch = props.routeData.id.match(/T(\d{2}:\d{2})/) 
            if (timeMatch && timeMatch[1]) {
              constructedId += `|${timeMatch[1]}`
            }
          }
          
          return constructedId
        }
        
        return ''
      })
      
      return {
        isOpen,
        formattedResponse,
        formatTime,
        buss,
        chevron,
        totalStations,
        totalTransfers,
        toggleOpen,
        hasTravelInfo,
        travelInfoTitle,
        travelInfoContent,
        isRebooking,
        hasMissingProductTypes,
        journeyId
      }
    },
  })
</script>

<style lang="scss" scoped>
  .journey-list-details {
    $el: &;
    flex-grow: 0;
    overflow-y: auto;

    &__title {
      @include title-small;
      margin-bottom: 1rem;
      text-align: left;
    }

    &__leg {
      position: relative;
      display: flex;
      flex-direction: column;
    }

    &__line {
      width: 3px;
      background-color: $clr-primary;
      position: absolute;
      top: 3px;
      bottom: 10px;
      left: 60px;
      z-index: 0;
    }

    &__gap {
      position: relative;
      &::before {
        content: '';
        position: absolute;
        left: 60px;
        top: -31px;
        bottom: -20px;
        width: 3px;
        background-color: $clr-white;
        z-index: 1;
      }
    }

    &__station {
      display: flex;
      align-items: baseline;
      position: relative;

      &:last-child #{$el}__time {
        margin-bottom: 0;
      }
    }

    &__time {
      flex: 0 0 60px;
      text-align: left;
      margin-bottom: 27px;
      color: $clr-text;
    }

    &__icon-container {
      display: flex;
      align-items: center;
      margin: 0 10px;
    }

    &__bus-icon {
      width: 20px;
      height: 20px;
      color: $clr-primary;
      position: absolute;
      left: -9px;
      top: -18px;
      z-index: 1;
    }

    &__dot-container {
      position: relative;
    }

    &__dot {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background-color: $clr-white;
      border: 4px solid $clr-primary;
      position: absolute;
      top: -12px;
      left: -5px;
      z-index: 1;
    }

    &__station-details {
      display: flex;
      flex-direction: column;
    }

    &__name {
      color: $clr-text;
      margin-left: 1.5rem;
      text-align: left;
    }

    &__meta-data {
      margin-top: 0.313rem;
      text-align: left;
    }

    &__platform {
      margin-top: 0.313rem;
      text-align: left;
      color: $clr-text;
    }

    &__waiting-time {
      margin-bottom: 1rem;
      color: $clr-text;
      @include title-medium;
    }

    &__accordion-button {
      @include body-medium;
      background: none;
      border: none;
      color: $clr-primary;
      font-size: 1rem;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.375rem 1rem;
      margin: 0.75rem 0 0.75rem 1.5rem;
      border-radius: 9999px;
      box-shadow: $box-shadow-card;
    }

    &__chevron {
      margin-left: 0.5rem;
      transition: transform 0.3s;
      width: 16px;
      height: 16px;
      transform: rotate(180deg);
    }

    &__chevron--open {
      transform: rotate(0deg);
    }

    &__stations {
      &-enter-active,
      &-leave-active {
        transition: all 0.3s ease-out;
      }

      &-enter-from {
        opacity: 0;
        transform: translateY(-20px);
      }

      &-leave-to {
        opacity: 0;
        transform: translateY(20px);
      }

      &-move {
        transition: transform 0.3s ease-out;
      }
    }
  }
</style>